/* ==========================================================================
   WAVE HAWAII · Gratis-Geschenk-Block (Fusion Agency, 29.10.2026)
   Eigene Darstellung über dem Qikify-Fortschritt (App-Block "Order goal").
   Alle Werte kommen aus Qikify: fehlender Betrag, nächstes Geschenk, Fortschritt,
   Stufen (Name + Position). Die Euro-Schwellen späterer Stufen werden aus
   Warenkorbwert (/cart.js) + fehlendem Betrag berechnet.
   Klappt etwas nicht, bleibt einfach die normale Qikify-Anzeige sichtbar.
   Eingebunden in snippets/buy-buttons.liquid und sections/cart-drawer.liquid (für die Schnellansicht),
   Gestaltung in assets/wh-geschenk.css bzw. assets/wh-quickbuy.css.
   ========================================================================== */
(() => {
  if (window.whGeschenk) return;
  window.whGeschenk = true;

  const ICONS = {
    socks: '<path d="M7 2.5h8v3H7z"/><path d="M7 5.5v7.7a2 2 0 0 0 .6 1.4l5 5a3.3 3.3 0 0 0 4.7-4.7L15 12.6V5.5"/><path d="M12.4 15.6l2.6-2.6"/>',
    cap: '<path d="M3.5 15.5a8.5 8.5 0 0 1 17 0z"/><path d="M12 7v8.5"/><path d="M20.5 15.5h1a1 1 0 0 1 0 2H12"/>',
    gift: '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>',
    check: '<path d="M5 12l5 5L20 7"/>'
  };

  const svg = (name, size) =>
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`;

  const iconFor = (title) => {
    if (/socke/i.test(title)) return 'socks';
    if (/\bcap\b|kappe|mütze|muetze|beanie/i.test(title)) return 'cap';
    return 'gift';
  };

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const parseMoney = (text) => {
    const m = String(text).replace(/\s/g, '').match(/(\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?)/);
    return m ? parseFloat(m[1].replace(/\./g, '').replace(',', '.')) : NaN;
  };

  const formatMoney = (value) => {
    const rounded = Math.round(value * 100) / 100;
    const whole = Math.abs(rounded - Math.round(rounded)) < 0.005;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency', currency: 'EUR',
      minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2
    }).format(rounded);
  };

  // "90,00€" → "90,00 €"
  const tidyAmount = (text) => String(text).trim().replace(/\s*€/, ' €');

  let cartTotal = null;
  const fetchCart = async () => {
    try {
      const res = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      cartTotal = (cart.items_subtotal_price ?? cart.total_price) / 100;
    } catch (e) {
      cartTotal = null;
    }
  };

  const readQikify = (goal) => {
    const progressEl = goal.querySelector('.qbk-order-goal__progress');
    const progressRaw = progressEl ? (progressEl.style.getPropertyValue('--qbk-order-goal-process') || getComputedStyle(progressEl).getPropertyValue('--qbk-order-goal-process')) : '';
    const progress = Math.max(0, Math.min(100, parseFloat(progressRaw) || 0));

    const milestones = [...goal.querySelectorAll('.qbk-order-goal__milestone')].map((el) => ({
      title: (el.querySelector('.qbk-order-goal__title')?.textContent || '').trim(),
      left: parseFloat(el.style.left) || 0
    })).filter((m) => m.title).sort((a, b) => a.left - b.left);

    const message = goal.querySelector('.qbk-order-goal__promote-message');
    const highlights = message ? [...message.querySelectorAll('.qbk-order-goal__message--highlight')].map((h) => h.textContent.trim()) : [];
    const amountText = highlights.find((h) => /\d/.test(h) && /€|eur/i.test(h)) || '';
    const goalTitle = highlights.find((h) => h !== amountText) || '';

    return { progress, milestones, amountText, goalTitle, messageText: (message?.textContent || '').trim() };
  };

  const build = (data) => {
    const { progress, milestones } = data;
    if (!milestones.length) return null;

    milestones.forEach((m) => { m.reached = progress >= m.left - 0.01; });
    const reached = milestones.filter((m) => m.reached);
    const upcoming = milestones.filter((m) => !m.reached);
    const next = upcoming[0];

    // Euro-Schwellen: nächste Stufe = Warenkorb + fehlender Betrag; daraus die volle Skala (Position in %)
    const remaining = parseMoney(data.amountText);
    let scaleMax = null;
    if (next && next.left > 0 && cartTotal != null && !isNaN(remaining)) {
      scaleMax = (cartTotal + remaining) / (next.left / 100);
    }
    milestones.forEach((m) => { m.amount = scaleMax ? scaleMax * m.left / 100 : null; });

    const money2 = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.max(0, v));

    let html = '<div class="wh-gift__box">';

    // Ein eigenes Element je Stufe: mobil untereinander, ab Tablet nebeneinander
    html += '<div class="wh-gift__goals">';
    milestones.forEach((m) => {
      const state = m.reached ? 'reached' : (m === next ? 'next' : 'locked');
      let text;
      if (m.reached) {
        text = `<span class="wh-gift__name">${esc(m.title)}</span> freigeschaltet!`;
      } else if (m === next && data.amountText) {
        text = `Noch <strong>${esc(tidyAmount(data.amountText))}</strong> bis zu <span class="wh-gift__name">${esc(m.title)}</span>`;
      } else if (m.amount != null && cartTotal != null) {
        text = `Noch <strong>${money2(m.amount - cartTotal)}</strong> bis zu <span class="wh-gift__name">${esc(m.title)}</span>`;
      } else {
        text = `Danach: <span class="wh-gift__name">${esc(m.title)}</span>`;
      }
      html += `<div class="wh-gift__goal wh-gift__goal--${state}"><span class="wh-gift__goal-icon">${svg(m.reached ? 'check' : iconFor(m.title), 18)}</span><p>${text}</p></div>`;
    });
    html += '</div>';

    // Solange die nächste Stufe nicht erreicht ist, endet der Balken vor ihrem Kreis (Radius 22 px + 6 px Luft)
    const fillWidth = next ? `min(${progress}%, max(0px, calc(${next.left}% - 28px)))` : `${progress}%`;

    html += `<div class="wh-gift__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(progress)}" aria-label="Fortschritt bis zu den Geschenken"><div class="wh-gift__fill" style="width:${fillWidth}"></div>`;
    milestones.forEach((m) => {
      const state = m.reached ? 'reached' : (m === next ? 'next' : 'locked');
      html += `<span class="wh-gift__step wh-gift__step--${state}" style="left:${m.left}%" title="${esc(m.title)}">${svg(iconFor(m.title), 28)}${m.reached ? `<span class="wh-gift__step-check">${svg('check', 10)}</span>` : ''}</span>`;
    });
    html += '</div>';

    html += '</div>';
    return html;
  };

  const init = (blockItem) => {
    let ui = null;
    let timer = null;
    let observer = null;

    const render = async () => {
      const goal = blockItem.querySelector('.qbk-order-goal');
      if (!goal) return;
      await fetchCart();
      const html = build(readQikify(goal));
      if (!html) return;

      if (!ui) {
        ui = document.createElement('div');
        ui.className = 'wh-gift';
        ui.setAttribute('aria-live', 'polite');
        blockItem.appendChild(ui);
      }
      if (ui.innerHTML !== html) ui.innerHTML = html;
      goal.setAttribute('aria-hidden', 'true');
      blockItem.classList.add('wh-gift-ready');
    };

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(render, 120);
    };

    observer = new MutationObserver((mutations) => {
      if (mutations.every((m) => ui && ui.contains(m.target))) return; // eigene Änderungen ignorieren
      schedule();
    });
    observer.observe(blockItem, { subtree: true, childList: true, attributes: true, characterData: true, attributeFilter: ['style', 'class'] });

    document.addEventListener('cart:change', schedule);
    document.addEventListener('cart:refresh', schedule);
    schedule();
  };

  // Produktvorschläge mit "Unter der Galerie anzeigen": das Theme zeigt sie mobil erst nach allen Produktinfos
  // (hinter Beschreibung usw.). Bis 999 px rücken sie deshalb direkt unter die Geschenk-Box, ab Desktop zurück unter die Galerie.
  const keepUpsellUnderGift = (giftItem) => {
    const section = giftItem.closest('.shopify-section--main-product');
    const rec = section && section.querySelector('.product-content-below-gallery product-recommendations[intent="complementary"]');
    if (!rec) return;

    const mq = window.matchMedia('(max-width: 999px)');
    const marker = document.createComment('wh-upsell-original-position');
    rec.before(marker);
    const slot = document.createElement('div');
    slot.className = 'product-info__block-item wh-upsell-slot';
    slot.dataset.blockType = 'complementary-products';

    const apply = () => {
      if (mq.matches) {
        if (rec.parentElement !== slot) { giftItem.after(slot); slot.appendChild(rec); }
      } else if (rec.parentElement === slot) {
        marker.after(rec);
        slot.remove();
      }
    };
    mq.addEventListener('change', apply);
    apply();
  };

  // Produktseite und Schnellansicht (Quick Buy): jeder Qikify-Block wird einmal eingerichtet,
  // auch wenn die Schnellansicht ihn erst später (per Ajax) einfügt.
  const SELECTOR = '.shopify-section--main-product .product-info__block-item[data-block-id*="qikify"], .quick-buy-modal .product-info__block-item[data-block-id*="qikify"]';

  const setup = () => {
    document.querySelectorAll(SELECTOR).forEach((item) => {
      if (item.dataset.whGift) return;
      item.dataset.whGift = '1';
      init(item);
      keepUpsellUnderGift(item);
    });
  };

  const start = () => {
    setup();
    let pending = null;
    new MutationObserver(() => {
      if (pending) return;
      pending = requestAnimationFrame(() => { pending = null; setup(); });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
